import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import RevealText from "../components/RevealText";
import ScrollReveal from "../components/ScrollReveal";
import KorvNet from "../components/KorvNet";
import KorvBottle from "../components/KorvBottle";
import KorvCandle from "../components/KorvCandle";
import KorvJar from "../components/KorvJar";
import KorvVessel from "../components/KorvVessel";
import { BATH_SALT_SCENTS } from "../lib/bathSalts";
import { KOTCHA_IMAGE, BATH_SALT_IMAGE } from "../lib/api";
import { toast } from "sonner";

/**
 * Universal detail page for RitualObject entries — kotcha, cleanser, cream, candle, ...
 * Adapts the visual to the object kind and offers a variant selector where relevant.
 */
export default function RitualObjectDetail() {
  const { slug } = useParams();
  const [obj, setObj] = useState(null);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setObj(null); setSelected(null);
    api.get(`/ritual-objects/${slug}`)
      .then((r) => {
        setObj(r.data);
        setSelected(r.data.variants?.[0]?.code || null);
      })
      .catch(() => {});
  }, [slug]);

  if (!obj) {
    return (
      <div className="min-h-[100svh] bg-ink text-bone flex items-center justify-center">
        <span className="text-[11px] uppercase tracking-editorial text-bone/55">Loading —</span>
      </div>
    );
  }

  const activeVariant = obj.variants?.find((v) => v.code === selected) || obj.variants?.[0];
  const activePrice = activeVariant?.price_display || obj.price_from;
  const heroColor = obj.kind === "kotcha" && activeVariant?.swatch_hex ? activeVariant.swatch_hex : obj.hero_swatch_hex;

  return (
    <div className="bg-ink text-bone" data-testid={`ritual-obj-page-${obj.slug}`}>
      {/* Meta */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-24 md:pt-28 py-6 flex items-center justify-between text-[10px] uppercase tracking-editorial text-bone/60">
        <span>Deux Rituels · Chapter · {obj.chapter.toUpperCase()}</span>
        <Link to="/rituals" className="editorial-link !text-bone !border-bone/60">Back to Deux Rituels</Link>
      </div>
      <div className="h-px bg-bone/15" />

      {/* ═══ HERO ═══ */}
      <section className="grid grid-cols-12 min-h-[90svh]">
        <div className="col-span-12 md:col-span-6 relative overflow-hidden" style={{ background: obj.kind === "salt" ? "#3a3430" : shade(obj.hero_swatch_hex, -8) }}>
          <div aria-hidden className="absolute inset-0" style={{ background: obj.kind === "salt"
            ? "radial-gradient(60% 60% at 50% 45%, rgba(255,240,220,0.18), transparent 68%)"
            : "radial-gradient(55% 55% at 50% 45%, rgba(255,240,220,0.12), transparent 68%)" }} />
          <div aria-hidden className="absolute inset-x-0 bottom-0 h-[26%]" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.5))" }} />

          <AnimatePresence mode="wait">
            <motion.div
              key={selected || obj.slug}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 1.1, ease: [0.2, 0.7, 0.1, 1] }}
              className="absolute inset-0 flex items-center justify-center p-8 md:p-16"
            >
              <ObjectHero obj={obj} heroColor={heroColor} activeVariant={activeVariant} />
            </motion.div>
          </AnimatePresence>

          <div className="absolute left-6 top-6 text-[10px] uppercase tracking-editorial text-bone/70">
            {obj.kind.toUpperCase()}
          </div>
          <div className="absolute right-6 top-6 text-[10px] uppercase tracking-editorial text-bone/60">
            {activeVariant?.label || ""}
          </div>
        </div>

        <div className="col-span-12 md:col-span-6 flex flex-col justify-between p-8 md:p-16 md:pl-20 relative">
          <div className="text-[10px] uppercase tracking-editorial text-bone/60 flex items-center justify-between">
            <span>korv.</span>
            <span>{obj.subtitle}</span>
          </div>

          <div className="my-12 md:my-0">
            <p className="text-[11px] uppercase tracking-eyebrow mb-6" style={{ color: obj.accent_hex }}>
              {obj.tagline}
            </p>
            <RevealText
              as="h1"
              className="font-serif-display text-[16vw] md:text-[9vw] leading-[0.86] tracking-[-0.03em]"
              lines={[obj.name]}
            />
            {obj.kind === "kotcha" && (
              <p className="mt-4 text-[13px] uppercase tracking-editorial text-bone/60">/kot.tʃa/</p>
            )}
            <p className="mt-10 text-[16px] leading-[1.65] max-w-[46ch] text-bone/80">
              {obj.description_long}
            </p>

            {/* Variant selector */}
            {obj.variants && obj.variants.length > 1 && (
              <div className="mt-10">
                <p className="text-[10px] uppercase tracking-editorial text-bone/55 mb-4">
                  {obj.kind === "kotcha" || obj.kind === "towel" ? "Colour" : "Composition"}
                </p>
                {(obj.kind === "kotcha" || obj.kind === "towel") ? (
                  <ColourGrid variants={obj.variants} selected={selected} setSelected={setSelected} />
                ) : (
                  <div className="grid grid-cols-1 gap-3 max-w-md">
                    {obj.variants.map((v) => (
                      <button
                        key={v.code}
                        type="button"
                        onClick={() => setSelected(v.code)}
                        data-testid={`variant-${v.code}`}
                        className={`text-left px-5 py-4 border transition-all duration-500 ${
                          selected === v.code ? "border-bone bg-bone/10" : "border-bone/30 hover:border-bone/70"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-[13px] uppercase tracking-editorial">{v.label}</span>
                          {v.price_display && <span className="chapter-num text-[15px]">{v.price_display}</span>}
                        </div>
                        {v.volume && <p className="mt-1 text-[11px] text-bone/55 tracking-wide">{v.volume}</p>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="mt-10 flex items-end gap-8">
              <span className="chapter-num text-4xl md:text-5xl">{activePrice}</span>
              <button
                data-testid="obj-enquire"
                onClick={() => setOpen(true)}
                className="editorial-link text-[11px] uppercase tracking-editorial pb-2 border-b border-bone"
              >
                Reserve · Enquire
              </button>
            </div>
          </div>

          <div className="text-[10px] uppercase tracking-editorial text-bone/55 flex items-center justify-between">
            <span>Filled by hand · Charlotte</span>
            <span>Weighted matte glass</span>
            <span>Made for the daily ritual</span>
          </div>
        </div>
      </section>

      {/* ═══ FORMULATION / MATERIALS ═══ */}
      {obj.kind === "salt" && (
        <BathSaltCollectionStrip obj={obj} selected={selected} setSelected={setSelected} />
      )}

      {obj.formulation_note && (
        <section className="py-24 md:py-32 border-t border-bone/15">
          <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
            <ScrollReveal className="col-span-12 md:col-span-5">
              <p className="text-[10px] uppercase tracking-eyebrow text-bone/55 mb-6">
                {obj.kind === "kotcha" ? "The Material" : "On the formulation"}
              </p>
              <h3 className="font-serif-display text-3xl md:text-5xl leading-[1.05] max-w-[20ch]">
                Considered, <span className="italic font-serif-italic">restorative</span>, made to last.
              </h3>
            </ScrollReveal>
            <ScrollReveal delay={0.1} className="col-span-12 md:col-span-6 md:col-start-7">
              <p className="text-[17px] leading-[1.7] text-bone/80 max-w-[52ch]">{obj.formulation_note}</p>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* ═══ RITUAL ═══ */}
      {obj.ritual_steps?.length > 0 && (
        <section className="py-24 md:py-32 border-t border-bone/15">
          <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-5">
              <p className="text-[10px] uppercase tracking-eyebrow text-bone/55 mb-8">The Ritual</p>
              <h3 className="font-serif-display text-3xl md:text-5xl leading-[1.05] max-w-[16ch]">
                A gesture repeated, <span className="italic font-serif-italic">slowly</span>.
              </h3>
            </div>
            <ol className="col-span-12 md:col-span-6 md:col-start-7 space-y-8">
              {obj.ritual_steps.map((step, i) => (
                <ScrollReveal key={i} delay={i * 0.05}>
                  <li className="border-b border-bone/20 pb-6">
                    <div className="text-[10px] uppercase tracking-editorial text-bone/55">Step {i + 1}</div>
                    <p className="font-serif-display text-2xl md:text-3xl leading-[1.15] mt-2">{step}</p>
                  </li>
                </ScrollReveal>
              ))}
            </ol>
          </div>
        </section>
      )}

      {open && <EnquireDialog obj={obj} variant={activeVariant} onClose={() => setOpen(false)} />}
    </div>
  );
}

/* ═══ Bath Salt — The Five Rituals horizontal editorial table ═══ */
function BathSaltCollectionStrip({ obj, selected, setSelected }) {
  const scents = obj.variants || [];
  return (
    <section className="py-24 md:py-32 border-t border-bone/15">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between mb-14 md:mb-20">
          <div>
            <p className="text-[10px] uppercase tracking-eyebrow text-bone/55 mb-6">The Five Rituals</p>
            <h3 className="font-serif-display text-4xl md:text-6xl leading-[0.95] max-w-[26ch]">
              One vessel. <span className="italic font-serif-italic">Five</span> landscapes.
            </h3>
          </div>
          <p className="hidden md:block text-[10px] uppercase tracking-editorial text-bone/50">N° 001 — 005</p>
        </div>

        {/* Column headings — desktop only */}
        <div className="hidden md:grid grid-cols-12 gap-6 pb-4 border-b border-bone/25 text-[10px] uppercase tracking-editorial text-bone/45">
          <div className="col-span-1">N°</div>
          <div className="col-span-2">Ritual</div>
          <div className="col-span-2">Feeling</div>
          <div className="col-span-3">Landscape</div>
          <div className="col-span-3">Composition</div>
          <div className="col-span-1 text-right">Palette</div>
        </div>

        <ul>
          {scents.map((v, i) => {
            const s = BATH_SALT_SCENTS[v.code];
            if (!s) return null;
            const active = selected === v.code;
            return (
              <li
                key={v.code}
                className={`border-b border-bone/20 transition-colors duration-500 ${active ? "bg-bone/[0.04]" : ""}`}
              >
                <button
                  type="button"
                  onClick={() => setSelected(v.code)}
                  data-testid={`salt-scent-${v.code}`}
                  className="w-full text-left group py-8 md:py-10 px-0 md:px-2 grid grid-cols-12 gap-6 items-center"
                >
                  {/* N° */}
                  <div className="col-span-3 md:col-span-1 flex items-center gap-3">
                    <span className="chapter-num text-2xl md:text-3xl" style={{ color: s.accent }}>
                      0{i + 1}
                    </span>
                  </div>

                  {/* Ritual name */}
                  <div className="col-span-9 md:col-span-2">
                    <h4 className="font-serif-display text-3xl md:text-4xl leading-[1] group-hover:italic transition-all duration-700">
                      {s.label}
                    </h4>
                  </div>

                  {/* Feeling */}
                  <div className="col-span-6 md:col-span-2">
                    <p className="text-[10px] uppercase tracking-editorial text-bone/70 leading-[1.55]">
                      {s.feeling}
                    </p>
                  </div>

                  {/* Landscape — italic subtitle */}
                  <div className="col-span-6 md:col-span-3">
                    <p className="font-serif-italic italic text-[15px] md:text-[16px] leading-[1.35] text-bone/85 max-w-[28ch]">
                      {s.subtitle}
                    </p>
                  </div>

                  {/* Composition — the note list */}
                  <div className="col-span-9 md:col-span-3">
                    <p className="text-[12px] md:text-[13px] text-bone/70 leading-[1.55] max-w-[34ch]">
                      {s.notes.join(" · ")}
                    </p>
                  </div>

                  {/* Palette dots + selected indicator */}
                  <div className="col-span-3 md:col-span-1 flex flex-col items-end gap-3">
                    <div className="flex items-center gap-1.5">
                      {s.palette.slice(0, 5).map((c, ci) => (
                        <span key={ci}
                          className="inline-block w-2.5 h-2.5 rounded-full"
                          style={{ background: c, boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.2)" }}
                        />
                      ))}
                      {s.botanicals.slice(0, 2).map((c, ci) => (
                        <span key={"b"+ci}
                          className="inline-block w-1.5 h-2.5 rounded-[2px]"
                          style={{ background: c }}
                        />
                      ))}
                    </div>
                    {active ? (
                      <span className="text-[9px] uppercase tracking-editorial" style={{ color: s.accent }}>
                        · Selected
                      </span>
                    ) : (
                      <span className="text-[9px] uppercase tracking-editorial text-bone/40 group-hover:text-bone/80 transition-colors">
                        Select →
                      </span>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

/* ────────── Object hero visual ────────── */
function ObjectHero({ obj, heroColor, activeVariant }) {
  const scent = activeVariant?.label?.toUpperCase() || "";
  if (obj.kind === "kotcha") {
    // Use the house reference photograph. Full-bleed fill of the left canvas.
    return (
      <img
        src={KOTCHA_IMAGE}
        alt="KOTCHA — the luxury African bath net"
        className="absolute inset-0 w-full h-full object-cover"
      />
    );
  }
  if (obj.kind === "candle") {
    return <KorvCandle color={obj.hero_swatch_hex} accent={obj.accent_hex} scent={scent} animate={false} className="max-h-[80vh] w-auto" />;
  }
  if (obj.kind === "cream" || obj.kind === "soak") {
    return (
      <div className="w-[60%] md:w-[52%]">
        <KorvJar color={obj.hero_swatch_hex} accent={obj.accent_hex} variant="signature"
                 sublabel={`${obj.name.toUpperCase()} · ${scent}`} animate={false} />
      </div>
    );
  }
  if (obj.kind === "salt") {
    // Bath Salt — the house reference photograph is the hero.
    return (
      <img
        src={BATH_SALT_IMAGE}
        alt="Deux Rituels bath salt — ceramic vessels"
        className="absolute inset-0 w-full h-full object-cover"
      />
    );
  }
  if (obj.kind === "cleanser" || obj.kind === "oil" || obj.kind === "serum" || obj.kind === "diffuser" || obj.kind === "spray" || obj.kind === "incense") {
    return (
      <KorvBottle color={obj.hero_swatch_hex} accent={obj.accent_hex}
        productLabel={obj.name.toUpperCase()}
        notes={activeVariant ? [activeVariant.label] : (obj.variants?.slice(0,3).map(v=>v.label) || [])}
        volume={activeVariant?.volume || ""}
        animate={false} className="max-h-[80vh] w-auto" />
    );
  }
  if (obj.kind === "towel") {
    return (
      <svg viewBox="0 0 320 340" className="max-h-[80vh] w-auto" aria-hidden>
        {(obj.variants || []).slice(0, 5).map((v, i) => (
          <g key={v.code}>
            <rect x="40" y={70 + i * 42} width="240" height="36" rx="2" fill={v.swatch_hex || "#D9CFC0"} />
            <rect x="40" y={70 + i * 42} width="240" height="4" fill="rgba(0,0,0,0.10)" />
            <rect x="40" y={70 + i * 42 + 32} width="240" height="4" fill="rgba(0,0,0,0.20)" />
            <text x="270" y={92 + i * 42} textAnchor="end"
              fill={isDark(v.swatch_hex || "#D9CFC0") ? "rgba(237,230,216,0.85)" : "rgba(20,18,17,0.75)"}
              fontFamily="Fraunces, serif" fontWeight="500" fontSize="14">korv.</text>
          </g>
        ))}
      </svg>
    );
  }
  if (obj.kind === "stone") {
    return (
      <svg viewBox="0 0 320 320" className="max-h-[80vh] w-auto" aria-hidden>
        <defs>
          <radialGradient id="stg1" cx="35%" cy="30%" r="70%">
            <stop offset="0" stopColor="rgba(255,255,255,0.16)" />
            <stop offset="0.7" stopColor="rgba(0,0,0,0)" />
            <stop offset="1" stopColor="rgba(0,0,0,0.32)" />
          </radialGradient>
        </defs>
        <path d="M 70 160 C 42 100, 110 60, 160 62 C 220 66, 275 118, 260 178 C 250 232, 175 254, 130 240 C 84 226, 92 192, 70 160 Z"
          fill={obj.hero_swatch_hex} />
        <path d="M 70 160 C 42 100, 110 60, 160 62 C 220 66, 275 118, 260 178 C 250 232, 175 254, 130 240 C 84 226, 92 192, 70 160 Z"
          fill="url(#stg1)" />
      </svg>
    );
  }
  return null;
}

/* ────────── Colour swatch grid (KOTCHA + Ritual Towel) ────────── */
function ColourGrid({ variants, selected, setSelected }) {
  return (
    <div className="flex flex-wrap gap-3 max-w-lg">
      {variants.map((v) => (
        <button
          key={v.code}
          type="button"
          onClick={() => setSelected(v.code)}
          data-testid={`variant-${v.code}`}
          className={`flex flex-col items-center transition-transform duration-500 ${
            selected === v.code ? "translate-y-[-2px]" : ""
          }`}
        >
          <span
            className="block w-12 h-12 md:w-14 md:h-14"
            style={{
              background: v.swatch_hex,
              boxShadow: selected === v.code
                ? "inset 0 0 0 1px rgba(255,255,255,0.15), 0 0 0 1px hsl(var(--bone))"
                : "inset 0 0 0 1px rgba(255,255,255,0.10)",
            }}
          />
          <span className="mt-2 text-[9px] uppercase tracking-editorial text-bone/60">{v.label}</span>
        </button>
      ))}
    </div>
  );
}

/* ────────── Enquire dialog ────────── */
function EnquireDialog({ obj, variant, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [state, setState] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setState("loading");
    try {
      await api.post("/enquiries", {
        product_slug: `object-${obj.slug}`,
        variant: variant?.code || null,
        name, email, message,
      });
      toast.success("Received. The house will be in quiet correspondence.");
      setState("ok");
      setTimeout(onClose, 900);
    } catch {
      toast.error("A quiet issue. Kindly try again.");
      setState("idle");
    }
  };

  return (
    <motion.div
      data-testid="obj-enquire-dialog"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-ink/75 backdrop-blur-sm flex items-center justify-center px-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.2, 0.7, 0.1, 1] }}
        className="w-full max-w-xl bg-bone text-ink p-8 md:p-12 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-[11px] uppercase tracking-editorial">Close</button>
        <p className="text-[10px] uppercase tracking-eyebrow text-ink/60 mb-4">Reserve · Enquire</p>
        <h3 className="font-serif-display text-3xl md:text-5xl leading-[1.05] mb-2">{obj.name}</h3>
        <p className="text-[13px] text-ink/70 mb-8">
          {variant?.label ? `${variant.label} · ` : ""}{variant?.price_display || obj.price_from}
        </p>
        <form onSubmit={submit} className="space-y-6">
          <input data-testid="obj-name" value={name} onChange={(e)=>setName(e.target.value)} required placeholder="Your name" className="field" />
          <input data-testid="obj-email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required placeholder="Your address" className="field" />
          <textarea data-testid="obj-message" value={message} onChange={(e)=>setMessage(e.target.value)} placeholder="A note (optional)" rows={3} className="field resize-none" />
          <button data-testid="obj-submit" disabled={state==="loading"} className="mt-2 text-[11px] uppercase tracking-editorial pb-2 border-b border-ink">
            {state === "loading" ? "Sending —" : "Send Enquiry"}
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
}

function shade(hex, percent) {
  const c = hex.replace("#", "");
  let r = parseInt(c.slice(0, 2), 16);
  let g = parseInt(c.slice(2, 4), 16);
  let b = parseInt(c.slice(4, 6), 16);
  const f = percent / 100;
  r = Math.max(0, Math.min(255, Math.round(r + 255 * f)));
  g = Math.max(0, Math.min(255, Math.round(g + 255 * f)));
  b = Math.max(0, Math.min(255, Math.round(b + 255 * f)));
  return "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("");
}
function isDark(hex) {
  const c = (hex || "#000").replace("#", "");
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return (r * 0.299 + g * 0.587 + b * 0.114) < 140;
}
